import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ButtonV2, Modal } from '@asigna/kit';
import { applyDefaultEmail, getEmailSettings, setEmailSettings } from 'api';
import { AddEmailDialog, Switch } from 'components';
import { useIsProActivated, useReadOnlyWarning } from 'hooks';
import MailIcon from 'images/mail.svg';
import { useCurrentSafe } from 'store';
import { EmailMultisigSettings, EmailMultisigSettingsResponse, EmailSettingsFlags } from 'types';

import { EMAIL_SETTINGS } from '../constants';

export const EmailSettings = () => {
  const history = useHistory();
  const { currentSafe } = useCurrentSafe();
  const [emailValue, setEmailValue] = useState('');
  const [defaultEmail, setDefaultEmail] = useState('');
  const [isAddEmailDialogOpen, setAddEmailDialogOpen] = useState(false);
  const [emailMultisigSettings, setEmailMultisigSettings] = useState<EmailMultisigSettings>();

  const isActivatedPro = useIsProActivated();
  const readOnlyWarning = useReadOnlyWarning();

  const updateEmailSettings = (response: EmailMultisigSettingsResponse) => {
    setEmailMultisigSettings(response.data?.multisig);
    setEmailValue(response.data?.multisig?.email?.email || '');
    if (response.data?.user?.email?.email) {
      setDefaultEmail(response.data.user.email.email);
    }
  };

  useEffect(() => {
    if (!currentSafe?._id) return;

    getEmailSettings(currentSafe._id).then(updateEmailSettings);
  }, [currentSafe?._id]);

  const handleChangeEmailSettings = (settingType: EmailSettingsFlags, flag: boolean) => {
    setEmailMultisigSettings((prevState) => {
      if (!prevState || !prevState.email) return prevState;

      const updatedEmail = {
        ...prevState.email,
        [settingType]: flag,
      };

      return {
        ...prevState,
        email: updatedEmail,
      };
    });

    setEmailSettings(emailValue, currentSafe?._id || '', settingType, flag).then((res) => {
      setEmailMultisigSettings(res.data?.multisig);
    });
  };

  const handleSetDefaultEmail = () => {
    if (!currentSafe?._id) return;
    applyDefaultEmail(currentSafe._id).then((result) => {
      if (result.success) {
        getEmailSettings(currentSafe._id).then(updateEmailSettings);
      }
    });
  };

  const handleAddEmailDialogClose = (shouldUpdate?: boolean) => {
    setAddEmailDialogOpen(false);
    if (shouldUpdate) {
      getEmailSettings(currentSafe._id).then(updateEmailSettings);
    }
  };

  const switchWarningText = !emailValue ? 'Email not configured' : readOnlyWarning;

  return (
    <>
      <div className="flex flex-col p-24 gap-12 sm:gap-32 rounded-[12px] border-[.5px] border-[#FFE341] bg-[linear-gradient(91deg,_#1C190C_0%,_#14100C_100%)] mb-48 sm:mb-8">
        <div className="flex flex-col items-start gap-12">
          <div className="flex w-full items-start justify-between flex-col sm:flex-row gap-12 sm:gap-0">
            <div className="flex items-center gap-8">
              <p className="text-m16 text-white">Email Notifications</p>
              {/* <div className="rounded-[24px] bg-[linear-gradient(103deg,_#FFE341_0%,_#F7E47D_24.5%,_#F1C855_48.99%,_#FFE2A8_73.49%,_#FFE970_97.99%)] w-44 h-24 flex items-center text-[#493B15] justify-center">
                Pro
              </div> */}
            </div>
            {Boolean(emailValue) ? (
              <ButtonV2
                size="sm"
                color="secondary"
                onClick={() => setAddEmailDialogOpen(true)}
                disabled={Boolean(readOnlyWarning)}
                disableHint={readOnlyWarning}
                className="rounded-[24px] border-0"
              >
                <div className="flex items-center gap-x-[8px]">
                  <img src={MailIcon} alt="" />
                  <span className="text-r13">{emailValue}</span>
                  <span className="text-r14 text-primary">Change</span>
                </div>
              </ButtonV2>
            ) : (
              <div className="flex gap-12">
                <div className="flex items-center gap-8">
                  <div className="w-4 h-4 rounded-[4px] bg-error" />
                  <span className="text-error text-r13">Email not configured</span>
                </div>
                <ButtonV2
                  color="secondary"
                  message="Add email"
                  size="md"
                  onClick={() => setAddEmailDialogOpen(true)}
                  disabled={Boolean(readOnlyWarning)}
                  disableHint={readOnlyWarning}
                />
                {Boolean(defaultEmail) && (
                  <ButtonV2
                    color="secondary"
                    size="md"
                    onClick={handleSetDefaultEmail}
                    disabled={Boolean(readOnlyWarning)}
                    disableHint={readOnlyWarning}
                  >
                    Use Default
                  </ButtonV2>
                )}
              </div>
            )}
          </div>

          <div className="text-r13 sm:text-r14">
            Get real-time email notifications for all important Vault activities — including
            transaction updates, message signature requests, and security alerts.
          </div>
          {!isActivatedPro && (
            <div>
              <button
                className="text-primary"
                onClick={() => history.push(`/vault/${currentSafe?.address}/pro`)}
              >
                Upgrade
              </button>{' '}
              to Pro to Enable full range of available Email Notifications.
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-24 sm:gap-32 items-center">
          {EMAIL_SETTINGS.map((emailSetting) => (
            <Switch
              onCheck={() =>
                handleChangeEmailSettings(
                  emailSetting.field,
                  !(emailMultisigSettings?.email?.[emailSetting.field] ?? true)
                )
              }
              checked={emailMultisigSettings?.email?.[emailSetting.field] ?? false}
              disabled={
                emailSetting.field === 'asignaUpdates'
                  ? !emailValue
                  : !isActivatedPro || !emailValue
              }
              disabledHint={switchWarningText}
              hintPosition="bottom-start"
              key={emailSetting.title}
            >
              {emailSetting.title}
            </Switch>
          ))}
        </div>
      </div>
      <Modal onClose={() => setAddEmailDialogOpen(false)} isShow={isAddEmailDialogOpen}>
        <AddEmailDialog
          onClose={handleAddEmailDialogClose}
          onSuccess={updateEmailSettings}
          currentMail={emailValue}
        />
      </Modal>
    </>
  );
};
